import React, { useState } from 'react'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { supabase } from '../../config/supabase'
import { useNavigate } from 'react-router-dom'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const navigate = useNavigate()

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      if (data.user) {
        navigate('/')
      }
    } catch (err: any) {
      setError(err.message || 'Login fejlede. Prøv igen.')
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      setError('Indtast venligst din email adresse')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) throw error

      setSuccess('Tjek din email! Vi har sendt dig et link til at nulstille din adgangskode.')
      setShowForgotPassword(false)
    } catch (err: any) {
      setError(err.message || 'Der opstod en fejl. Prøv igen.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'primary.main',
        padding: 2,
      }}
    >
      <Paper 
        elevation={0}
        sx={{ 
          p: 5, 
          width: 450, 
          maxWidth: '95%',
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            Find Bedemand
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Log ind for at administrere din platform 🔐
          </Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
            }}
          >
            {success}
          </Alert>
        )}

        <form onSubmit={showForgotPassword ? handleForgotPassword : handleLogin}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoComplete="email"
            sx={{ mb: 2 }}
          />
          {!showForgotPassword && (
            <TextField
              fullWidth
              type="password"
              label="Adgangskode"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              sx={{ mb: 1 }}
            />
          )}
          {!showForgotPassword && (
            <Box sx={{ textAlign: 'right', mb: 2 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setShowForgotPassword(true)
                  setError(null)
                }}
                sx={{ textTransform: 'none', fontSize: '0.875rem' }}
              >
                Glemt adgangskode?
              </Button>
            </Box>
          )}
          {showForgotPassword && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Indtast din email adresse, og vi sender dig et link til at nulstille din adgangskode.
            </Alert>
          )}
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ 
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : showForgotPassword ? (
              'Send Nulstillings-link'
            ) : (
              'Log ind'
            )}
          </Button>
        </form>

        {showForgotPassword && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setShowForgotPassword(false)
                setError(null)
                setSuccess(null)
              }}
              sx={{ textTransform: 'none' }}
            >
              ← Tilbage til login
            </Button>
          </Box>
        )}

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Har du brug for hjælp? Kontakt support
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}

